import { CalendarDays, Search, User, Users } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { memberPath, ministryPath } from '@/app/paths';
import { canOpenEvent } from '@/components/EventSheet';
import { useOverlays } from '@/components/Overlays';
import { cn } from '@/design-system/lib/cn';
import { formatShortDate, normalize } from '@escala/domain/dates';
import { useStore } from '@/store/AppStore';

type Result = { key: string; label: string; sub: string; icon: typeof User; go: () => void };

/** Busca de membros, próximos eventos e ministérios no cabeçalho (a partir de 2 letras). */
export default function GlobalSearch() {
  const { state, lookup, permissions, today, actions } = useStore();
  const { openEvent } = useOverlays();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);

  const results = useMemo<Result[]>(() => {
    const q = normalize(query.trim());

    if (q.length < 2) return [];

    const out: Result[] = [];

    state.members
      .filter((m) => normalize(m.name).includes(q))
      .slice(0, 4)
      .forEach((m) =>
        out.push({
          key: `m-${m.id}`,
          label: m.name,
          sub:
            Object.keys(m.roles)
              .map((id) => lookup.ministries.get(id)?.name)
              .filter(Boolean)
              .join(', ') || 'Membro',
          icon: User,
          go: () => navigate(memberPath(m.id)),
        })
      );

    state.events
      .filter((e) => e.date >= today && normalize(e.title).includes(q))
      .slice(0, 3)
      .forEach((e) =>
        out.push({
          key: `e-${e.id}`,
          label: e.title,
          sub: `${formatShortDate(e.date)} · ${e.time}`,
          icon: CalendarDays,
          go: () =>
            canOpenEvent(permissions.isManager, e, permissions.memberId)
              ? openEvent(e.id)
              : actions.showToast(
                  'Você não está escalado neste evento. Os detalhes ficam disponíveis apenas nos eventos em que você serve.'
                ),
        })
      );

    if (permissions.isManager) {
      state.ministries
        .filter((m) => normalize(m.name).includes(q))
        .forEach((m) =>
          out.push({
            key: `n-${m.id}`,
            label: m.name,
            sub: `Ministério · ${m.roles.length} funções`,
            icon: Users,
            go: () => navigate(ministryPath(m.id)),
          })
        );
    }

    return out;
  }, [actions, lookup, navigate, openEvent, permissions, query, state, today]);

  const showList = open && query.trim().length >= 2;

  return (
    <div
      ref={box}
      className="relative w-[min(360px,32vw)]"
      onBlur={(e) => {
        if (!box.current?.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 stroke-[1.8] text-ink-placeholder"
      />
      <input
        type="search"
        role="combobox"
        aria-label="Buscar"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        placeholder="Buscar membros, eventos, ministérios..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setQuery('');
            setOpen(false);
          }
        }}
        className="h-10 w-full rounded-control border border-line-input bg-white pr-3 pl-9 text-control placeholder:text-ink-placeholder"
      />
      {showList ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Resultados da busca"
          className="absolute inset-x-0 top-11.5 z-[36] rounded-block border border-line-input bg-white p-1.5 shadow-popover"
        >
          {results.map((r) => {
            const Icon = r.icon;

            return (
              <button
                key={r.key}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => {
                  r.go();
                  setQuery('');
                  setOpen(false);
                }}
                className={cn(
                  'flex min-h-10 w-full items-center gap-2.5 rounded-control-sm px-2.5 py-1.5 text-left hover:bg-surface-muted'
                )}
              >
                <Icon aria-hidden className="size-4 shrink-0 stroke-[1.8] text-ink-subtle" />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-small font-medium">{r.label}</span>
                  <span className="truncate text-caption text-ink-subtle">{r.sub}</span>
                </span>
              </button>
            );
          })}
          {!results.length ? <p className="m-0 p-2.5 text-small text-ink-subtle">Nenhum resultado.</p> : null}
        </div>
      ) : null}
    </div>
  );
}
